/* ──────────────────────────────────────────────────────────────────────
   ServiceLandingPage — /<service-slug>/ deep-dive landing
   ──────────────────────────────────────────────────────────────────────
   ONE component renders all 5 service landing pages (arch-bim, mep-bim,
   clash-detection, scan-to-bim, hbim). Data comes from
   window.DZ_SERVICE_LANDINGS keyed by slug.

   Slug detection: each /<slug>/index.html declares
       <ServiceLandingPage slug="arch-bim" />
   so we don't need URL parsing — the HTML shell carries the intent.

   Section rhythm (matches the existing /ai/ and /vision-2030/ pages):
     Hero (light) → Problem (navy) → Delivers (aqua-wash) →
     Approach (navy) → Featured project (light) → Related stages (navy)
     → CtaStrip (reused) → Footer (reused)
   ────────────────────────────────────────────────────────────────────── */

const { useEffect: useSlpEffect, useRef: useSlpRef } = React;

function useSlpReveal() {
  const ref = useSlpRef(null);
  useSlpEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add('is-in-view');
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function SlpReveal({ as: Tag = 'div', className = '', children, ...rest }) {
  const ref = useSlpReveal();
  return (
    <Tag ref={ref} className={`dz-slp-reveal ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

function ServiceLandingPage({ slug }) {
  const data = (window.DZ_SERVICE_LANDINGS || {})[slug];
  // Inbound link up to the BIM pillar. Every service landing is a BIM discipline,
  // so this reads true on all of them; guarded off on /bim/ itself (AR /bim/ also
  // renders through this component) to avoid a self-link. Locale via the site idiom.
  const isAr = (typeof window !== 'undefined') && window.location.pathname.startsWith('/ar/');
  const pillarHref = (isAr ? '/ar' : '') + '/bim/';

  // If the slug isn't found in data, render a minimal fallback rather
  // than a blank page. Defensive — shouldn't happen in production but
  // protects against typos in the HTML shell.
  if (!data) {
    return (
      <main className="dz-slp dz-slp--missing" id="dz-main">
        <h1>Service landing not found</h1>
        <p>Slug "{slug}" has no data in window.DZ_SERVICE_LANDINGS.</p>
        <a href="/services/">← Back to all services</a>
      </main>
    );
  }

  return (
    <main className={`dz-slp dz-slp--${slug}`} id="dz-main">

      {/* ── HERO (light) ─────────────────────────────────────────── */}
      <section className="dz-slp__hero">
        <div className="dz-slp__hero-inner">
          <SlpReveal>
            <span className="dz-slp__eyebrow dz-slp__eyebrow--aqua">
              {data.hero.eyebrow}
            </span>
            <h1 className="dz-slp__hero-title">
              {data.hero.title.lead}{' '}
              <em>{data.hero.title.accent}</em>
            </h1>
            <p className="dz-slp__hero-sub">{data.hero.sub}</p>
            <ul className="dz-slp__hero-statline" aria-label={data.hero.statlineAriaLabel || 'Service credentials'}>
              {data.hero.statline.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
            {data.hero.ctas && (
              <div className="dz-slp__hero-cta">
                <a className="dz-bim-btn dz-bim-btn--primary" href={data.hero.ctas.primary.href}>{data.hero.ctas.primary.label}</a>
                {data.hero.ctas.secondary && <a className="dz-bim-btn dz-bim-btn--ghost" href={data.hero.ctas.secondary.href}>{data.hero.ctas.secondary.label}</a>}
              </div>
            )}
          </SlpReveal>
        </div>
      </section>

      {data.projects && window.DzProjectCards && <window.DzProjectCards data={data.projects} id="bim-projects" />}

      {/* ── PROBLEM (navy) ───────────────────────────────────────── */}
      {data.problem && (
        <section className="dz-slp__problem">
          <div className="dz-slp__inner">
            <SlpReveal>
              <span className="dz-slp__eyebrow">{data.problem.eyebrow}</span>
              <h2 className="dz-slp__h2">{data.problem.title}</h2>
            </SlpReveal>
            <ol className="dz-slp__problem-list">
              {data.problem.points.map((pt, i) => (
                <SlpReveal as="li" key={i} className="dz-slp__problem-item">
                  <span className="dz-slp__problem-num">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="dz-slp__problem-h">{pt.h}</h3>
                  <p className="dz-slp__problem-p">{pt.p}</p>
                </SlpReveal>
              ))}
            </ol>
            {data.problem.ctaLink && <p className="dz-cta-link dz-cta-link--on-dark">{data.problem.ctaLink.text} <a href={data.problem.ctaLink.href}>{data.problem.ctaLink.label} {isAr ? '←' : '→'}</a></p>}
          </div>
        </section>
      )}

      {/* ── DELIVERS (aqua-wash) ─────────────────────────────────── */}
      {data.delivers && (
        <section className="dz-slp__delivers">
          <div className="dz-slp__inner">
            <SlpReveal>
              <span className="dz-slp__eyebrow dz-slp__eyebrow--aqua">
                {data.delivers.eyebrow}
              </span>
              <h2 className="dz-slp__h2 dz-slp__h2--navy">{data.delivers.title}</h2>
              {data.delivers.sub && (
                <p className="dz-slp__lead">{data.delivers.sub}</p>
              )}
            </SlpReveal>

            {/* Two-column LOD-split (Arch BIM uses both LOD 300 and 400) */}
            {data.delivers.lods && (
              <div className="dz-slp__lods">
                {data.delivers.lods.map((lod, i) => (
                  <SlpReveal as="article" key={i} className="dz-slp__lod">
                    <header className="dz-slp__lod-head">
                      <span className="dz-slp__lod-level">{lod.level}</span>
                      <a
                        className="dz-slp__lod-label"
                        href={lod.stageHref}
                        aria-label={`Open ${lod.label} stage page`}
                      >
                        {lod.label} →
                      </a>
                    </header>
                    <p className="dz-slp__lod-summary">{lod.summary}</p>
                    <ul className="dz-slp__lod-items">
                      {lod.items.map((it, j) => (
                        <li key={j}>{it}</li>
                      ))}
                    </ul>
                  </SlpReveal>
                ))}
              </div>
            )}

            {/* Flat deliverables list (used by pages without LOD split) */}
            {!data.delivers.lods && data.delivers.items && (
              <ul className="dz-slp__delivers-list">
                {data.delivers.items.map((it, i) => (
                  <SlpReveal as="li" key={i} className="dz-slp__delivers-item">
                    <span className="dz-slp__delivers-dot" aria-hidden="true" />
                    <span className="dz-slp__delivers-text">{it}</span>
                  </SlpReveal>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* ── APPROACH (navy) ──────────────────────────────────────── */}
      {data.approach && (
        <section className="dz-slp__approach" id={slug === 'aleen-152' ? 'aleen-approach' : slug === 'methodology' ? 'method-approach' : undefined}>
          <div className="dz-slp__inner">
            <SlpReveal>
              <span className="dz-slp__eyebrow">{data.approach.eyebrow}</span>
              <h2 className="dz-slp__h2">{data.approach.title}</h2>
              {data.approach.sub && (
                <p className="dz-slp__lead">{data.approach.sub}</p>
              )}
            </SlpReveal>
            <ol className="dz-slp__steps">
              {data.approach.steps.map((s, i) => (
                <SlpReveal as="li" key={i} className="dz-slp__step">
                  <span className="dz-slp__step-num">{s.num}</span>
                  <div className="dz-slp__step-body">
                    <h3 className="dz-slp__step-h">{s.h}</h3>
                    <p className="dz-slp__step-p">{s.p}</p>
                  </div>
                </SlpReveal>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ── FEATURED PROJECT (light) ─────────────────────────────── */}
      {data.featured && (
        <section className="dz-slp__featured">
          <div className="dz-slp__inner">
            <SlpReveal as="article" className="dz-slp__featured-card">
              <span className="dz-slp__eyebrow dz-slp__eyebrow--aqua">
                {data.featured.eyebrow}
              </span>
              <h2 className="dz-slp__h2 dz-slp__h2--navy">{data.featured.title}</h2>
              <p className="dz-slp__featured-summary">{data.featured.summary}</p>
              <ul className="dz-slp__featured-stats">
                {data.featured.stats.map((st, i) => (
                  <li key={i} className="dz-slp__featured-stat">
                    <span className="dz-slp__featured-stat-num">{st.num}</span>
                    <span className="dz-slp__featured-stat-label">{st.label}</span>
                  </li>
                ))}
              </ul>
              <a
                className="dz-slp__featured-cta"
                href={data.featured.href}
              >
                {data.featured.ctaLabel || 'Open case study'} <span aria-hidden="true">→</span>
              </a>
            </SlpReveal>
          </div>
        </section>
      )}

      {/* ── RELATED LIFECYCLE STAGES (navy) ──────────────────────── */}
      {data.relatedStages && data.relatedStages.length > 0 && (
        <section className="dz-slp__related">
          <div className="dz-slp__inner">
            <SlpReveal>
              <span className="dz-slp__eyebrow">{(data.relatedHeading && data.relatedHeading.eyebrow) || 'WHERE IT FITS'}</span>
              <h2 className="dz-slp__h2">
                {(data.relatedHeading && data.relatedHeading.titleLead) || 'In the 11-stage'} <em>{(data.relatedHeading && data.relatedHeading.titleAccent) || 'BIM lifecycle.'}</em>
              </h2>
              {slug !== 'bim' && (
                <p className="dz-slp__related-pillar">
                  {isAr
                    ? <>أحد تخصّصات منظومة <a href={pillarHref}>BIM المتكاملة</a> لدى ديزاين زون — من الفكرة إلى التسليم والتشغيل.</>
                    : <>One discipline within Design Zone&rsquo;s <a href={pillarHref}>end-to-end BIM</a> delivery — brief to operational handover.</>}
                </p>
              )}
              {data.relatedHeading && data.relatedHeading.ctaLink && <p className="dz-cta-link">{data.relatedHeading.ctaLink.text} <a href={data.relatedHeading.ctaLink.href}>{data.relatedHeading.ctaLink.label} {isAr ? '←' : '→'}</a></p>}
            </SlpReveal>
            <div className="dz-slp__related-grid">
              {data.relatedStages.map((st, i) => (
                <SlpReveal as="a" key={i}
                  className="dz-slp__related-card"
                  href={st.href || ((window.location.pathname.startsWith('/ar/') ? '/ar' : '') + '/services/' + st.slug + '/')}
                >
                  <span className="dz-slp__related-num">{st.num}</span>
                  <span className="dz-slp__related-h">{st.label}</span>
                  <span className="dz-slp__related-note">{st.note}</span>
                  <span className="dz-slp__related-arrow" aria-hidden="true">→</span>
                </SlpReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — added 2026-05-13 for SEO H5 (FAQPage rich results +
          GEO citation density). Renders only when data.faq is set on
          the page entry. FAQPage JSON-LD lives in the static shell. */}
      {data.faq && data.faq.items && data.faq.items.length > 0 && (
        <section className="dz-slp__section dz-slp__section--faq">
          <div className="dz-slp__inner">
            <div className="dz-slp__h2-row">
              <span className="dz-slp__eyebrow">
                {data.faq.eyebrow || 'FREQUENTLY ASKED'}
              </span>
              <h2 className="dz-slp__h2">{data.faq.title}</h2>
            </div>
            <dl className="dz-slp__faq">
              {data.faq.items.map((qa, i) => (
                <div className="dz-slp__faq-item" key={i}>
                  <dt className="dz-slp__faq-q">{qa.q}</dt>
                  <dd className="dz-slp__faq-a" dangerouslySetInnerHTML={{ __html: qa.a }} />
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

    </main>
  );
}

window.ServiceLandingPage = ServiceLandingPage;
